import classnames from 'classnames';
import React from 'react';

import { DomainConfigurationDto } from '@/types/DomainConfigurationDto';

import { withOpacity } from '@cineamo/ui/src/helpers/color-helper';

export type GiftCardTypeCardButton = {
    title?: string;
    trailingIcon?: React.ReactNode;
    onClick?: () => void;
    href?: string;
};

type GiftCardTypeCardProps = {
    icon?: string;
    title?: string;
    description?: string;
    button?: GiftCardTypeCardButton;
    isNew?: boolean;
    className?: string;
    domainConfiguration: DomainConfigurationDto;
};

const GiftCardTypeCard: React.FC<GiftCardTypeCardProps> = ({
    icon,
    title,
    description,
    button,
    isNew,
    className,
    domainConfiguration
}) => {
    const renderButton = () => {
        if (!button) return null;

        const buttonContent = (
            <>
                <span>{button.title}</span>
                {button.trailingIcon && (
                    <span className="inline-flex items-center justify-center size-[16px]">{button.trailingIcon}</span>
                )}
            </>
        );

        const buttonClassName = classnames(
            'inline-flex items-center justify-center text-body-14 font-bold hover:opacity-80 transition-opacity',
            !!button.trailingIcon && 'gap-8'
        );
        const buttonStyle = { color: domainConfiguration.style.primaryColor };

        if (button.href) {
            const isExternal = /^https?:\/\//.test(button.href);
            return (
                <a
                    className={buttonClassName}
                    href={button.href}
                    onClick={button.onClick}
                    target={isExternal ? '_blank' : undefined}
                    rel={isExternal ? 'noopener noreferrer' : undefined}
                    style={buttonStyle}>
                    {buttonContent}
                </a>
            );
        }

        return (
            <button
                className={buttonClassName}
                type="button"
                onClick={button.onClick}
                style={buttonStyle}>
                {buttonContent}
            </button>
        );
    };

    return (
        <div
            className={classnames(
                'relative flex flex-col text-center px-8 py-24 rounded-16 transition-all duration-300',
                !isNew && 'shadow-lg hover:shadow-xl',
                'hover:scale-101',
                className
            )}
            style={{
                backgroundColor: domainConfiguration.style.surfaceColor,
                ...(isNew && {
                    boxShadow: `0 0 0 1px ${withOpacity(domainConfiguration.style.primaryColor, 0.5)}, 0 0 16px 4px ${withOpacity(domainConfiguration.style.primaryColor, 0.4)}, 0 4px 16px ${withOpacity(domainConfiguration.style.primaryColor, 0.2)}`
                })
            }}>
            {isNew && (
                <div
                    className="absolute -top-12 -right-12 flex items-center justify-center size-[64px] rotate-12 drop-shadow-md"
                    style={{
                        clipPath:
                            'polygon(100% 50%, 86.7% 59.8%, 93.3% 75%, 76.9% 76.9%, 75% 93.3%, 59.8% 86.7%, 50% 100%, 40.2% 86.7%, 25% 93.3%, 23.1% 76.9%, 6.7% 75%, 13.3% 59.8%, 0% 50%, 13.3% 40.2%, 6.7% 25%, 23.1% 23.1%, 25% 6.7%, 40.2% 13.3%, 50% 0%, 59.8% 13.3%, 75% 6.7%, 76.9% 23.1%, 93.3% 25%, 86.7% 40.2%)',
                        backgroundColor: domainConfiguration.style.primaryColor
                    }}>
                    <span
                        className="text-body-12 font-bold"
                        style={{ color: domainConfiguration.style.onPrimaryColor }}>
                        Neu!
                    </span>
                </div>
            )}
            <div
                className="size-[48px] mx-auto rounded-full flex items-center justify-center"
                style={{ backgroundColor: domainConfiguration.style.primaryColor }}>
                <span
                    className="text-[24px]"
                    style={{ color: domainConfiguration.style.onPrimaryColor }}>
                    {icon}
                </span>
            </div>
            <h3
                className="text-headline-20 mt-16"
                style={{ color: domainConfiguration.style.onSurfaceColor }}>
                {title}
            </h3>
            <p
                className="text-body-14 flex-1 mt-8"
                style={{ color: withOpacity(domainConfiguration.style.onSurfaceColor, 0.8) }}>
                {description}
            </p>
            {button && <div className="mt-16">{renderButton()}</div>}
        </div>
    );
};

export default GiftCardTypeCard;
